import { Injectable } from '@angular/core';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {TranslateService} from '@ngx-translate/core';
import {ToastHandlerService} from '../../shared/services/toast-handler.service';
import {environment} from '../../../environments/environment';
import {catchError, map, tap} from 'rxjs/operators';
import {lastValueFrom, throwError} from 'rxjs';
import {BackendFilteringObject} from '../../shared/models/grid-config.interface';
import {BackendPaginationResponse} from '../../shared/models/backend-pagination-response';
import * as moment from 'moment/moment';
import {Newsletter, NEWSLETTER_RECURRENCE, NEWSLETTER_ZONE} from '../../shared/models/newsletter';
import {ExportCsvService} from '../../shared/services/export-csv.service';
import {USER_ROLE} from '../../shared/models/user';

@Injectable({
  providedIn: 'root'
})
export class AdminNewslettersService {

  constructor(private http: HttpClient,
              private translateService: TranslateService,
              private toastService: ToastHandlerService,
              private exportService: ExportCsvService) { }

  public getRolesList() {
    return [{
      value: USER_ROLE.ADMIN,
      name: this.translateService.instant('admin.newsletters.roles.admin')
    }, {
      value: USER_ROLE.VALET,
      name: this.translateService.instant('admin.newsletters.roles.valet')
    }, {
      value: USER_ROLE.CUSTOMER_READ_ONLY,
      name: this.translateService.instant('admin.newsletters.roles.customer_user_read_only')
    }, {
      value: USER_ROLE.CUSTOMER_USER,
      name: this.translateService.instant('admin.newsletters.roles.customer_user')
    }, {
      value: USER_ROLE.CUSTOMER_ADMIN,
      name: this.translateService.instant('admin.newsletters.roles.customer_admin')
    }];
  }

  public getZonesList() {
    return [{
      value: NEWSLETTER_ZONE.DASHBOARD,
      name: 'Tableau de bord',
      roles: [USER_ROLE.ADMIN, USER_ROLE.VALET, USER_ROLE.CUSTOMER_READ_ONLY, USER_ROLE.CUSTOMER_USER, USER_ROLE.CUSTOMER_ADMIN]
    }, {
      value: NEWSLETTER_ZONE.ORDER_CREATION_DATE,
      name: 'Création de commande (Date)',
      roles: [USER_ROLE.ADMIN, USER_ROLE.CUSTOMER_READ_ONLY, USER_ROLE.CUSTOMER_USER, USER_ROLE.CUSTOMER_ADMIN]
    }, {
      value: NEWSLETTER_ZONE.ORDER_CREATION_ADDRESSES,
      name: 'Création de commande (Adresses)',
      roles: [USER_ROLE.ADMIN, USER_ROLE.CUSTOMER_READ_ONLY, USER_ROLE.CUSTOMER_USER, USER_ROLE.CUSTOMER_ADMIN]
    }, {
      value: NEWSLETTER_ZONE.EVALUATION,
      name: 'Évaluation',
      roles: [USER_ROLE.VALET]
    }];
  }

  public getRecurrenceList() {
    return [{
      value: NEWSLETTER_RECURRENCE.NONE,
      name: 'Ne pas répéter'
    }, {
      value: NEWSLETTER_RECURRENCE.EACH_YEAR,
      name: 'Répéter chaque année'
    }, {
      value: NEWSLETTER_RECURRENCE.EACH_MONTH,
      name: 'Répéter chaque mois'
    }];
  }

  public getAlertLevels() {
    return [{
      value: 'danger',
      name: 'Très élevé (rouge)'
    }, {
      value: 'warning',
      name: 'Élevé (orange)'
    }, {
      value: 'info',
      name: 'Moyen (bleu foncé)'
    }, {
      value: 'success',
      name: 'Faible (vert)'
    }];
  }

  public getNewsletters(filteringObj: BackendFilteringObject, filter: any | null): Promise<BackendPaginationResponse<Newsletter>> {
    let params = new HttpParams();
    if (filter) {
      Object.keys(filter).forEach(key => {
        if (filter[key]) {
          params = params.append(key, filter[key]);
        }
      });
    }
    const request$ = this.http.get<BackendPaginationResponse<Newsletter>>(
      `${environment.backendUrl}admin/get-newsletters?paginate=true&${filteringObj.getBackendFiltering()}`,
      { params }
    ).pipe(
      catchError(err => {
        this.toastService.showErrorToastsResponse(err);
        return throwError(() => err);
      })
    );

    return lastValueFrom(request$);
  }

  public getAlerts(filteringObj: BackendFilteringObject, filter: any | null): Promise<BackendPaginationResponse<Newsletter>> {
    let params = new HttpParams();
    if (filter) {
      Object.keys(filter).forEach(key => {
        if (filter[key]) params = params.append(key, filter[key]);
      });
    }
    const request$ = this.http.get<BackendPaginationResponse<Newsletter>>(
      `${environment.backendUrl}admin/get-alerts?paginate=true&${filteringObj.getBackendFiltering()}`,
      { params }
    ).pipe(
      catchError(err => {
        this.toastService.showErrorToastsResponse(err);
        return throwError(() => err);
      })
    );
    return lastValueFrom(request$);
  }

  public uploadImage(formData: FormData): Promise<any> {
    return this.http.post(`${environment.backendUrl}static-file`, formData)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('admin.newsletters.image_loaded')
          )
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToasts(
            this.translateService.instant('admin.newsletters.image_not_loaded')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public exportNewslettersListPageData(localData: any[]): Promise<any> {
    try {
      const formattedExport = this.exportNewslettersListParser(localData);
      this.exportService.export(formattedExport);
      return Promise.resolve(formattedExport);
    } catch (error) {
      this.toastService.showErrorToasts(
        this.translateService.instant('admin.modules.valet.services.valet.export_error')
      );
      return Promise.reject(error);
    }
  }

  private exportNewslettersListParser(dataToExport: any[]): any[] {
    const formattedExport = [];

    dataToExport.forEach(row => {
      let cleanContent = '';

      if (row.content) {
        let processed = row.content;
        processed = processed.replace(/<\/(h[1-6])>/gi, ' - ');
        processed = processed.replace(/<\/(p|div|li|tr)>/gi, ' ');
        processed = processed.replace(/<br\s*\/?>/gi, ' ');

        const doc = new DOMParser().parseFromString(processed, 'text/html');
        cleanContent = (doc.body.textContent || '').replace(/\s+/g, ' ').trim();

        if (cleanContent.length > 50) {
          cleanContent = cleanContent.substring(0, 50) + '...';
        }
      }

      formattedExport.push({
        'Id': row.id,
        'Contenu': cleanContent,
        'Date de création': row.creationDate ? moment(row.creationDate).format('DD/MM/YYYY HH:mm') : ''
      });
    });

    return formattedExport;
  }

  public exportAlertsListPageData(localData: any[]): Promise<any> {
    try {
      const formattedExport = this.exportAlertsListParser(localData);
      this.exportService.export(formattedExport);
      return Promise.resolve(formattedExport);
    } catch (error) {
      this.toastService.showErrorToasts(
        this.translateService.instant('modules.dashboard.export.export_error')
      );
      return Promise.reject(error);
    }
  }

  private exportAlertsListParser(dataToExport: any[]): any[] {
    const formattedExport = [];

    const rolesList = this.getRolesList();
    const zonesList = this.getZonesList();
    const levelsList = this.getAlertLevels();
    const recurrenceList = this.getRecurrenceList();

    const getLabel = (list: any[], value: string) => {
      if (!value || value === 'NONE') return 'Ne pas répéter';
      const found = list.find(item => item.value === value);
      return found ? found.name : value;
    };

    dataToExport.forEach(row => {
      let cleanContent = '';
      if (row.content) {
        let processed = row.content;
        processed = processed.replace(/<\/(h[1-6])>/gi, ' - ');
        processed = processed.replace(/<\/(p|div|li|tr)>/gi, ' ');
        processed = processed.replace(/<br\s*\/?>/gi, ' ');

        const doc = new DOMParser().parseFromString(processed, 'text/html');
        cleanContent = (doc.body.textContent || '').replace(/\s+/g, ' ').trim();

        if (cleanContent.length > 50) {
          cleanContent = cleanContent.substring(0, 50) + '...';
        }
      }

      const rolesReadable = (row.rolesArray || [])
        .map(roleVal => getLabel(rolesList, roleVal))
        .join(', ');

      const zonesReadable = (row.zonesArray || [])
        .map(zoneVal => getLabel(zonesList, zoneVal))
        .join(', ');

      formattedExport.push({
        'Id': row.id,
        'Niveau (vigilance)': getLabel(levelsList, row.alertLevel),
        'Contenu': cleanContent,
        'Date de création': row.creationDate ? moment(row.creationDate).format('DD/MM/YYYY HH:mm') : '',
        'Date de début': row.startDate ? moment(row.startDate).format('DD/MM/YYYY') : '',
        'Date de fin': row.endDate ? moment(row.endDate).format('DD/MM/YYYY') : 'Permanent',
        'Destinataire(s)': rolesReadable,
        'Emplacement(s)': zonesReadable,
        'Récurrence': getLabel(recurrenceList, row.recurrence)
      });
    });

    return formattedExport;
  }

  public exportNewsletters(filteringObj?: BackendFilteringObject): Promise<void> {
    const filterParams = filteringObj ? `&${filteringObj.getBackendFiltering()}` : '';

    const request$ = this.http.get(`${environment.backendUrl}admin/get-newsletters?exportCSV=true${filterParams}`, {
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      tap((response: HttpResponse<Blob>) => {
        if (!response.body) {
          return;
        }

        const filename = response.headers.get('Content-Disposition')?.match(/filename="?([^";\n]+)"?/)?.[1] || 'newsletters_export.csv';
        const url = window.URL.createObjectURL(response.body);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);

        this.toastService.showSuccessToast(
          this.translateService.instant('admin.newsletters.export_success')
        );
      }),
      catchError(err => {
        this.toastService.showErrorToastsResponse(err);
        return throwError(() => err);
      })
    );

    return lastValueFrom(request$).then(() => undefined);
  }

  public exportAlerts(filteringObj?: BackendFilteringObject): Promise<void> {
    const filterParams = filteringObj ? `&${filteringObj.getBackendFiltering()}` : '';

    const request$ = this.http.get(`${environment.backendUrl}admin/get-alerts?exportCSV=true${filterParams}`, {
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      tap((response: HttpResponse<Blob>) => {
        if (!response.body) return;
        const filename = response.headers.get('Content-Disposition')?.match(/filename="?([^";\n]+)"?/)?.[1] || 'alerts_export.csv';
        const url = window.URL.createObjectURL(response.body);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
        this.toastService.showSuccessToast(this.translateService.instant('admin.alerts.export_success'));
      }),
      catchError(err => {
        this.toastService.showErrorToastsResponse(err);
        return throwError(() => err);
      })
    );
    return lastValueFrom(request$).then(() => undefined);
  }

  public deleteNewsletter(id: string, type: 'newsletter' | 'alert'): Promise<any> {
    const request$ = this.http.delete(`${environment.backendUrl}admin/delete-newsletter/${id}`)
      .pipe(
        tap(() => {
          if (type === 'newsletter') {
            this.toastService.showSuccessToast(
              this.translateService.instant('admin.newsletters.grid.delete_success')
            );
          } else if (type === 'alert') {
            this.toastService.showSuccessToast(
              this.translateService.instant('admin.alerts.grid.delete_success')
            );
          }
        }),
        catchError(err => {
          return throwError(() => err);
        })
      );

    return lastValueFrom(request$);
  }

  public sendAlert(formData: any): Promise<any> {
    return this.http.post(`${environment.backendUrl}admin/alerts`, formData)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('admin.alerts.send_alert_success')
          )
          return res.data;
        }),
        catchError(err => {
          if (err.error?.errorCode === 'too_many_alerts') {
            this.toastService.showErrorToasts(
              this.translateService.instant('admin.alerts.too_many_alerts')
            );
          } else {
            this.toastService.showErrorToasts(
              this.translateService.instant('admin.alerts.send_alert_fail')
            );
          }
          return throwError(err);
        })
      ).toPromise();
  }

  public sendNewsletter(formData: any): Promise<any> {
    return this.http.post(`${environment.backendUrl}admin/newsletters`, formData)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('admin.newsletters.send_newsletter_success')
          )
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToasts(
            this.translateService.instant('admin.newsletters.send_newsletter_fail')
          );
          return throwError(err);
        })
      ).toPromise();
  }
}
